import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Calendar } from "lucide-react";
import { EXIT_INTENT } from "@/constants/testIds";
import { openCalendly } from "@/lib/constants";

const STORAGE_KEY = "smartedge-exit-intent-shown";
const MIN_ENGAGEMENT_MS = 8_000; // don't fire before user has been here 8s
const DESKTOP_MIN_WIDTH = 900;

/**
 * Exit-intent modal: fires once per session when the cursor leaves the top
 * of the viewport on desktop. Offers a soft "book 15 minutes with Dan" CTA
 * that opens the in-page Calendly widget.
 *
 * Frequency cap: sessionStorage flag — resets when the tab closes.
 */
export default function ExitIntent({
  eyebrow = "Before you go",
  headline = "Grab 15 minutes with Dan.",
  body = "A short call is the fastest way to see if SmartEdge can unlock hidden revenue in your business. No obligation, no upfront costs.",
  ctaLabel = "Schedule a 15-min call",
}) {
  const [visible, setVisible] = useState(false);

  const dismiss = useCallback(() => {
    setVisible(false);
  }, []);

  const bookCall = useCallback(() => {
    setVisible(false);
    openCalendly();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // Frequency cap: already shown this session
    try {
      if (window.sessionStorage.getItem(STORAGE_KEY) === "1") return undefined;
    } catch {
      // storage disabled — proceed anyway, still capped per page load
    }
    // Desktop only
    if (window.innerWidth < DESKTOP_MIN_WIDTH) return undefined;

    const mountedAt = Date.now();

    const trigger = () => {
      if (Date.now() - mountedAt < MIN_ENGAGEMENT_MS) return;
      setVisible(true);
      try {
        window.sessionStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* noop */
      }
      window.removeEventListener("mouseout", onMouseOut, true);
    };

    // mouseout fires when the pointer leaves the viewport;
    // relatedTarget is null when leaving the window entirely.
    const onMouseOut = (e) => {
      if (e.relatedTarget) return;
      if (e.clientY > 10) return; // only when leaving from the top
      trigger();
    };

    window.addEventListener("mouseout", onMouseOut, true);
    return () => window.removeEventListener("mouseout", onMouseOut, true);
  }, []);

  // ESC to close
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [visible, dismiss]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid={EXIT_INTENT.root}
          role="dialog"
          aria-modal="true"
          aria-labelledby="exit-intent-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 90,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <button
            type="button"
            aria-label="Dismiss"
            data-testid={EXIT_INTENT.backdrop}
            onClick={dismiss}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(6,27,49,0.55)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
              border: "none",
              cursor: "pointer",
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            style={{
              position: "relative",
              background: "#ffffff",
              borderRadius: 12,
              maxWidth: 520,
              width: "100%",
              padding: 40,
              boxShadow:
                "rgba(50,50,93,0.35) 0px 50px 100px -20px, rgba(0,0,0,0.3) 0px 30px 60px -30px",
              border: "1px solid var(--color-border)",
              overflow: "hidden",
            }}
          >
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: -80,
                right: -80,
                width: 200,
                height: 200,
                borderRadius: 999,
                background:
                  "radial-gradient(circle, rgba(83,58,253,0.14) 0%, rgba(83,58,253,0) 65%)",
              }}
            />
            <button
              type="button"
              aria-label="Close"
              data-testid={EXIT_INTENT.close}
              onClick={dismiss}
              style={{
                position: "absolute",
                top: 14,
                right: 14,
                width: 32,
                height: 32,
                border: "none",
                background: "transparent",
                color: "var(--color-body)",
                borderRadius: 6,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.15s ease, color 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--color-bg-soft)";
                e.currentTarget.style.color = "var(--color-heading)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--color-body)";
              }}
            >
              <X size={16} strokeWidth={2} />
            </button>

            <div
              className="flex items-center justify-center mb-5"
              style={{
                width: 48,
                height: 48,
                borderRadius: 10,
                background: "rgba(83,58,253,0.08)",
                color: "var(--color-purple)",
              }}
            >
              <Calendar size={22} strokeWidth={1.5} />
            </div>

            <div className="eyebrow mb-3">{eyebrow}</div>
            <h3
              id="exit-intent-title"
              className="type-subheading mb-3"
              style={{ fontSize: 26, letterSpacing: "-0.35px" }}
            >
              {headline}
            </h3>
            <p className="type-body mb-6" style={{ lineHeight: 1.55 }}>
              {body}
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button
                type="button"
                className="btn-primary btn-primary--lg"
                data-testid={EXIT_INTENT.cta}
                onClick={bookCall}
                autoFocus
              >
                {ctaLabel}
              </button>
              <button
                type="button"
                className="btn-ghost"
                data-testid={EXIT_INTENT.dismiss}
                onClick={dismiss}
              >
                Not right now
              </button>
            </div>

            <div
              className="type-caption mt-5"
              style={{ color: "var(--color-body)" }}
            >
              15 minutes · No obligation · Fully confidential
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
